import React, { forwardRef } from 'react';
import { format } from 'date-fns';
import { processImageFromFile } from '../../utils/imageCompression';
import { getRandomQuote } from '../../utils/motivationalQuotes';
import { supabase } from '../../lib/supabaseClient';

interface CameraCaptureProps {
  onCapture: (imageUrl: string) => void;
  onProcessing: (isProcessing: boolean) => void;
  recordDate: string;
}

/**
 * Native camera capture component - uses device camera app directly
 * Click triggers the phone's native camera for selfie, then processes and uploads
 */
const CameraCapture = forwardRef<HTMLInputElement, CameraCaptureProps>(({
  onCapture,
  onProcessing,
  recordDate
}, ref) => {
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    onProcessing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get current date/time and random quote
      const now = new Date();
      const dateTimeString = format(now, "MMM dd, yyyy 'at' hh:mm:ss a");
      const quote = getRandomQuote();

      // Process image: add watermark and compress
      const processedBlob = await processImageFromFile(
        file,
        dateTimeString,
        quote,
        {
          maxWidth: 1080,
          maxHeight: 1920,
          quality: 0.8
        }
      );

      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `${user.id}/${recordDate}_${timestamp}.jpg`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('Snapshots')
        .upload(fileName, processedBlob, {
          contentType: 'image/jpeg',
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      // Bucket is private; produce a time-limited signed URL for the callback.
      const { data: urlData, error: urlError } = await supabase.storage
        .from('Snapshots')
        .createSignedUrl(fileName, 3600);

      if (urlError) {
        throw urlError;
      }

      onCapture(urlData.signedUrl);
    } catch (err: any) {
      console.error('Camera capture error:', err);
      alert(err.message || 'Failed to capture and upload image. Please try again.');
    } finally {
      onProcessing(false);
      // Reset input so same file can be selected again
      e.target.value = '';
    }
  };

  return (
    <input
      ref={ref}
      type="file"
      accept="image/*"
      capture="user"
      onChange={handleFileChange}
      className="hidden"
      aria-label="Take selfie"
    />
  );
});

CameraCapture.displayName = 'CameraCapture';

export default CameraCapture;
